import React, { useEffect, useState, useMemo } from "react";
import styles from "./styles.css";
import { NUMBER } from "../../../constants/app-constants";
import PropTypes from "prop-types";
import { STEP_NAMES } from "../vehicle-information/constants";
import PopularBrands from "../popular-brands";
import { yieldToMain } from "../../../utils/helpers/yield-to-main";
import { SearchInput } from "@cars24/turbo-web/lib/search-input";
import { Label } from "@cars24/turbo-web/lib/label";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import { updateDisplayNameToLabel } from "./selector";
import { List } from "@cars24/turbo-web/lib/list";
import { sendCustomGaEvents } from "../../../tracking";
import { pdiBrandClicked } from "../../../constants/tracking-constants/pre-delivery-inspection";
import IconSpinner from "../../shared/icon-spinner";

const BrandList = ({
    brandList,
    fetchBrandsConnect,
    setDetailsConnect,
    brand
}) => {
    const { tokens } = useTokens();
    const [isLoading, setIsLoading] = useState(false);
    const [brands, setBrands] = useState(brandList);
    const [searchText, setSearchText] = useState("");
    const { id: selectedBrandId } = brand || {};

    useEffect(() => {
        if ((brandList || []).length > NUMBER.ZERO) {
            setIsLoading(false);
        } else {
            fetchBrandsConnect()
                .then(() => {
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }

        setBrands(brandList);
    }, [brandList, fetchBrandsConnect]);

    const handleOnChange = (searchQuery) => {
        setSearchText(searchQuery);
    };

    const handleSelectBrand = async({ name, id }) => {
        await yieldToMain();
        setDetailsConnect({
            [STEP_NAMES.Brand]: { name, id },
            ...(selectedBrandId !== id && {[STEP_NAMES.Model]: {}}),
            activeAccordionStepIndex: NUMBER.ONE,
            ownerName: ""
        });
        sendCustomGaEvents({
            ...pdiBrandClicked,
            eventLabel: name
        });
    };

    const handleCallback = async(selectedBrand) => {
        await yieldToMain();
        handleSelectBrand({
            name: selectedBrand.displayText,
            id: selectedBrand.id
        });
    };

    const handleSelectBrandClick = async (brandData) => {
        await yieldToMain();
        handleSelectBrand({
            name: brandData.displayName,
            id: brandData.make_id
        });
    };

    const filteredBrands = useMemo(() => {
        if (!searchText) {
            return brands;
        }
        return brands.filter((item) =>
            item.displayName && item.displayName.toLowerCase().includes(searchText.toLowerCase())
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText, brands]);

    const popularBrands = useMemo(
        () => filteredBrands?.filter((item) => item.is_popular === "yes" && item.logo),
        [filteredBrands]
    );
    // Has to be checked with design team about the loader
    if (isLoading) return <div styleName={"styles.loadingIcon"}><IconSpinner/></div>;

    return (
        <div>
            <div styleName={"styles.searchField"}>
                <SearchInput placeholder="Select car brand" value={searchText}  onChange={(e) => handleOnChange(e.target.value)} showCross={false} />
            </div>
            {((filteredBrands && filteredBrands.length) || (popularBrands && popularBrands.length)) ?
                <React.Fragment>
                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">Popular Brands</Label>
                    <PopularBrands
                        selected={selectedBrandId}
                        callback={handleCallback}
                        popularBrands={popularBrands}
                        isRevamped
                    />

                    <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">All Brands</Label>
                    <ul styleName={"styles.brandsList"}>
                        <List
                            items={updateDisplayNameToLabel(filteredBrands)}
                            onClick={(item) => {handleSelectBrandClick(item);}}
                        />
                    </ul>
                </React.Fragment>
                :
                <Label level={NUMBER.FOUR} color={tokens.text_default_text_secondary} fontWeight="regular">No results found</Label>
            }
        </div>
    );
};

export default BrandList;

BrandList.propTypes = {
    brandList: PropTypes.array,
    fetchBrandsConnect: PropTypes.func,
    setDetailsConnect: PropTypes.func,
    brand: PropTypes.object,
    setActiveAccordionStepIndexConnect: PropTypes.func
};
